// src/app/sign-in/[[...index]]/page.tsx
'use client';

import { SignIn, useUser } from '@clerk/nextjs';
import { useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { clerkAppearance } from '@/lib/clerkAppearance';

const AFTER_SIGN_IN =
  process.env.NEXT_PUBLIC_CLERK_AFTER_SIGN_IN_URL || '/';

export default function SignInPage() {
  const { isSignedIn, isLoaded } = useUser();
  const router = useRouter();
  const searchParams = useSearchParams();
  const raw = searchParams.get('redirect_url');
  let redirectUrl = AFTER_SIGN_IN;
  if (raw) {
    try {
      const url = new URL(raw);
      redirectUrl = url.pathname + url.search;
    } catch {
      redirectUrl = raw;
    }
  }

  const isRoomInvite = redirectUrl.startsWith('/rooms/');
  // 2026-07-13・CCO指摘：/editions経由の流入は大半が未登録購入者のため、
  // ルーム招待と同様にサインアップを目立たせる（見た目のみの変更・認証フローは不変）。
  const isEditionsLink = redirectUrl.startsWith('/editions');
  const showSignupCta = isRoomInvite || isEditionsLink;

  if (!isLoaded) return null;

  if (isSignedIn) {
    router.replace(redirectUrl);
    return null;
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <section className="w-full max-w-md mx-auto">
        <div className="w-full bg-white rounded-2xl p-6 shadow-sm">
          {/* ロゴ */}
          <div className="w-full py-2 mb-4 flex justify-center">
            <Image
              src="/kataruta-logo.png"
              alt="KATARUTA"
              width={160}
              height={44}
              priority
            />
          </div>

          {showSignupCta && (
            <div className="mb-4 text-center">
              {isRoomInvite ? (
                <>
                  <p className="text-base font-semibold text-gray-800">このルームに参加する</p>
                  <p className="mt-1 text-sm text-gray-500">このルームに招待されています。</p>
                </>
              ) : (
                <>
                  <p className="text-base font-semibold text-gray-800">はじめてご利用ですか？</p>
                  <p className="mt-1 text-sm text-gray-500">下のボタンから新規登録できます。</p>
                </>
              )}
              <a
                href={`/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}`}
                className="mt-3 inline-block w-full py-2 px-4 rounded-lg bg-gray-800 text-white text-sm font-medium text-center hover:bg-gray-700"
              >
                {isRoomInvite ? "新規登録して参加" : "新規登録する"}
              </a>
              <p className="mt-3 text-xs text-gray-400">すでにアカウントがある方は、下からログインできます。</p>
            </div>
          )}

          {/* Clerk（幅ズレ対策：w-fullで包む） */}
          <div className="w-full flex justify-center">
            <div className="w-full">
              <SignIn
                appearance={clerkAppearance}
                forceRedirectUrl={redirectUrl}
                fallbackRedirectUrl={redirectUrl}
              />
            </div>
          </div>

          {/* 行き来リンク */}
          {!showSignupCta && (
            <p className="mt-6 text-sm text-gray-500 text-center">
              まだアカウントがありませんか？{" "}
              <a href="/sign-up" className="underline">
                サインアップ
              </a>
            </p>
          )}
        </div>
      </section>
    </main>
  );
}
